import { useState } from 'react';
import { Table, Select, Tabs, Popover, Button, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Wallet, Hourglass, CheckCircle2, Percent, Download, Info } from 'lucide-react';
import { PageHeader, StatCard, QueryState, StatusBadge, SectionCard, EmptyState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { formatINR, formatDate } from '@/lib/format';
import { downloadText } from '@/platform/lib/csv';
import { labelOf } from '../lib/catalog';
import { orderBreakdown } from '../lib/settlements';

const LINE_TYPES = ['sale', 'commission', 'fee', 'tax', 'tcs', 'reversal', 'subscription'];
const STATES = ['pending', 'on_hold', 'payable', 'completed', 'reversed'];
const LINE_STATE_BADGE = { payable: 'scheduled', on_hold: 'pending', reversed: 'cancelled' };

/** Signed amount: credits plain, debits with a minus and the error tone. */
function Signed({ paise }) {
  return <span className={`tabular-nums font-medium ${paise < 0 ? 'text-error-600' : ''}`}>{paise < 0 ? `− ${formatINR(-paise)}` : formatINR(paise)}</span>;
}

/** How the line's order settles: its sale, each deduction and the net (MERCH-58). */
function Breakdown({ lines, line }) {
  const b = orderBreakdown(lines, line);
  return (
    // Inside a table row: keep the popover's clicks from reaching the row.
    <span role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Popover
        title={line.order_id ? `Order ${line.order_id}` : line.label}
        placement="bottom-end"
        trigger={<Button size="xs" variant="ghost" aria-label={`Fee breakdown for ${line.label}`} leftIcon={<Info size={14} />}>Breakdown</Button>}
      >
        <table className="text-sm min-w-[14rem]">
          <tbody>
            {b.rows.map((r) => (
              <tr key={r.id}>
                <td className="pr-4 py-0.5">{r.label}<span className="block text-xs opacity-60">{labelOf(r.type)}</span></td>
                <td className="text-right py-0.5"><Signed paise={r.amount_paise} /></td>
              </tr>
            ))}
            <tr className="border-t border-[var(--app-border)] font-semibold">
              <td className="pr-4 pt-1">Net to you</td>
              <td className="text-right pt-1"><Signed paise={b.net_paise} /></td>
            </tr>
          </tbody>
        </table>
      </Popover>
    </span>
  );
}

/**
 * SELL-07 — settlement center (real since D6, lib/finance/settlement): payable vs pending vs on hold vs paid,
 * commission/fee/GST/TCS/reversal lines from the platform policy, and the weekly payouts (D13).
 */
export default function Settlements() {
  const q = useQuery('merchantSettlements', {}, { select: (r) => r.data });
  const [state, setState] = useState('');
  const [type, setType] = useState('');
  const [tab, setTab] = useState(0);
  const { toast } = useToast();
  const exporter = useMutation('merchantSettlementExport');
  // Server-side CSV of every matching line (not just the 400 on screen); audited as settlement.export.
  const exportCsv = async () => {
    try {
      const r = await exporter.run({ ...(state ? { state } : {}), ...(type ? { type } : {}) });
      downloadText(r.data.filename, r.data.content);
      if (r.data.truncated) toast.warning('Export cut short', `Only the newest ${r.data.rows} lines are in the file. Narrow the filters to export the rest.`);
      else toast.success('Export ready', `${r.data.rows} line${r.data.rows === 1 ? '' : 's'}`);
    } catch { /* exporter.error */ }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Settlements" subtitle="Money from delivered orders, net of platform commission, gateway fees, GST and TCS. Paid out weekly to your verified bank account." />
      <QueryState query={q}>
        {(d) => {
          const lines = d.lines.filter((l) => (!state || l.state === state) && (!type || l.type === type));
          return (
            <>
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                <StatCard label="Payable" icon={<Wallet size={18} />} value={formatINR(d.payable_paise)} hint={`next payout ${formatDate(d.next_payout_at)}`} />
                <StatCard label="Pending" icon={<Hourglass size={18} />} value={formatINR(d.pending_paise)} hint={d.on_hold_paise ? `in the return window · ${formatINR(d.on_hold_paise)} on hold for returns` : 'in the return window'} />
                <StatCard label="Completed" icon={<CheckCircle2 size={18} />} value={formatINR(d.completed_paise)} hint="paid out" />
                <StatCard label="Fees & commission" icon={<Percent size={18} />} value={formatINR(d.fees_paise + d.commission_paise)} hint={`reversals ${formatINR(d.reversals_paise)}`} />
              </div>

              <Tabs index={tab} onChange={setTab} variant="line" orientation="horizontal">
                <Tabs.List className="border-b border-[var(--app-border)]">
                  <Tabs.Tab tabClassName="min-h-[44px]">Ledger lines</Tabs.Tab>
                  <Tabs.Tab tabClassName="min-h-[44px]">Payout history</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panels className="pt-4">
                  <Tabs.Panel>
                    <SectionCard>
                      <div className="grid gap-3 sm:grid-cols-2 lg:max-w-xl">
                        <Select placeholder="All states" options={STATES.map((s) => ({ value: s, label: labelOf(s) }))} value={state} onChange={setState} allowClear closeOnSelect />
                        <Select placeholder="All line types" options={LINE_TYPES.map((s) => ({ value: s, label: labelOf(s) }))} value={type} onChange={setType} allowClear closeOnSelect />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" leftIcon={<Download size={16} />} onClick={exportCsv} loading={exporter.loading}>Export CSV</Button>
                        <span className="text-xs opacity-60">Reconciliation rows for the filters above, with order and payout references.</span>
                      </div>
                      {exporter.error ? <Alert variant="error">{exporter.error}</Alert> : null}
                      {lines.length ? (
                        <Table
                          data={lines}
                          columns={[
                            { key: 'at', label: 'Date', render: (l) => formatDate(l.at) },
                            { key: 'order_id', label: 'Order', render: (l) => l.order_id || '—' },
                            { key: 'label', label: 'Line', wrap: true },
                            { key: 'type', label: 'Type', render: (l) => labelOf(l.type), hideOnMobile: true },
                            { key: 'amount_paise', label: 'Amount', render: (l) => <Signed paise={l.amount_paise} /> },
                            { key: 'state', label: 'State', render: (l) => <StatusBadge status={LINE_STATE_BADGE[l.state] || l.state} label={labelOf(l.state)} /> },
                            { key: 'breakdown', label: '', sortable: false, render: (l) => <Breakdown lines={d.lines} line={l} /> },
                          ]}
                          showSerial={false}
                          withAction={false}
                          showColumnPicker={false}
                          showReloadButton={false}
                          pagination
                          pageSize={25}
                        />
                      ) : <EmptyState title="No lines match">Clear a filter to see more.</EmptyState>}
                    </SectionCard>
                  </Tabs.Panel>
                  <Tabs.Panel>
                    <SectionCard>
                      <Table
                        data={d.payouts}
                        columns={[
                          { key: 'id', label: 'Payout', render: (p) => <span className="font-medium">{p.id}</span> },
                          { key: 'at', label: 'Date', render: (p) => formatDate(p.at) },
                          { key: 'amount_paise', label: 'Amount', render: (p) => <Signed paise={p.amount_paise} /> },
                          { key: 'bank', label: 'To', hideOnMobile: true },
                          { key: 'utr', label: 'UTR', render: (p) => <span className="font-mono text-xs">{p.utr || '—'}</span>, hideOnMobile: true },
                          { key: 'status', label: 'Status', render: (p) => <StatusBadge status={p.status} /> },
                        ]}
                        showSerial={false}
                        withAction={false}
                        showColumnPicker={false}
                        showReloadButton={false}
                      />
                    </SectionCard>
                  </Tabs.Panel>
                </Tabs.Panels>
              </Tabs>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
